/**
 * C-01 ダッシュボード（FR-37）。
 *
 * ★ **数値はすべて `GET /console/dashboard` から**（FR-82＝表示ハードコード禁止）。
 *   締めまでの残り分も**サーバが計算して返す**＝スタッフの PC の時計がズレていても同じ数字。
 * ★ 配送便カードの遷移先は **M1 では C-04（便で絞り込み）**（05 §6.1）。
 *   C-05 配送ボードは M2＝作らない。
 */

import { CHANNEL_LABEL, Card, ErrorNote, PageHeader, Skeleton } from "@kenzai/ui";
import { console_ } from "@kenzai/api-client";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

/** 3枚の数値カード＋2枚のカード＝**出来上がりと同じ形**で待つ（M1.7）。 */
const STAT_GRID = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: "var(--kz-space-4)",
  marginBottom: "var(--kz-space-5)",
} as const;

/** 下段2枚＝**狭い画面では縦に積む**（390px で 1fr 1fr のままだと中身が1文字ずつ折り返す）。 */
const PANEL_GRID = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
  gap: "var(--kz-space-4)",
} as const;

function DashboardSkeleton() {
  return (
    <>
      <PageHeader title="ダッシュボード" subtitle="今日の受注の状況" />
      <div style={STAT_GRID}>
        {[0, 1, 2].map((i) => (
          <Card key={i}>
            <Skeleton rows={1} height={14} width="40%" />
            <Skeleton rows={1} height={40} width="55%" style={{ marginTop: "var(--kz-space-2)" }} />
          </Card>
        ))}
      </div>
      <div style={PANEL_GRID}>
        {[0, 1].map((i) => (
          <Card key={i}>
            <Skeleton rows={1} height={18} width="45%" style={{ marginBottom: "var(--kz-space-4)" }} />
            <Skeleton rows={3} height={20} />
          </Card>
        ))}
      </div>
    </>
  );
}

export function Dashboard() {
  const q = useQuery({ queryKey: ["dashboard"], queryFn: () => console_.dashboard(), refetchInterval: 30_000 });

  if (q.isLoading) return <DashboardSkeleton />;
  if (q.isError) return <ErrorNote error={q.error} />;
  const d = q.data!;

  return (
    <>
      <PageHeader title="ダッシュボード" subtitle="今日の受注の状況" />

      <div style={STAT_GRID}>
        <Stat label="未処理" value={d.unconfirmed} to="/inbox" tone="primary" testId="stat-unconfirmed" />
        <Stat
          label="要確認"
          value={d.needs_review}
          to="/inbox"
          tone={d.needs_review > 0 ? "warn" : "neutral"}
          testId="stat-needs-review"
        />
        <Stat label="本日納品予定" value={d.due_today} to="/orders" tone="neutral" testId="stat-due-today" />
      </div>

      <div style={PANEL_GRID}>
        <Card>
          <h2 style={{ margin: "0 0 var(--kz-space-3)", fontSize: "var(--kz-font-size-md)" }}>
            配送便と締め
          </h2>
          {d.bins.length === 0 ? (
            <p style={{ color: "var(--kz-color-ink-muted)", fontSize: "var(--kz-font-size-sm)" }}>
              配送便が登録されていません。
            </p>
          ) : (
            <div style={{ display: "grid", gap: "var(--kz-space-2)" }}>
              {d.bins.map((b) => (
                // M1 の遷移先は C-04（便で絞り込み）＝C-05 配送ボードは M2（05 §6.1）
                // ★押せる行なのにホバーも押下も返していなかった（M1.7）＝`.kz-card-link`（浮き）＋
                //   `.kz-pressable`（沈み）を足す。**枠線と地色を inline style から外す**のが要点で、
                //   inline は必ずクラスに勝つのでホバーの色が出ない。
                <Link
                  key={b.bin_id}
                  to={`/orders?bin_id=${b.bin_id}`}
                  data-testid={`bin-card-${b.bin_id}`}
                  className="kz-card-link kz-pressable"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--kz-space-3)",
                    padding: "var(--kz-space-3)",
                    borderWidth: 1,
                    borderStyle: "solid",
                    borderRadius: "var(--kz-radius-sm)",
                  }}
                >
                  <strong>{b.name}</strong>
                  <span className="kz-num" style={{ color: "var(--kz-color-ink-muted)" }}>
                    {b.cutoff_time} 締め
                  </span>
                  <span style={{ marginLeft: "auto", textAlign: "right" }}>
                    {b.minutes_left === null ? (
                      <span style={{ color: "var(--kz-color-ink-muted)" }}>受付終了</span>
                    ) : (
                      <span
                        className="kz-num"
                        style={{
                          fontWeight: 700,
                          // 締め30分前を強調＝朝の山（NFR-01）で一番効く情報
                          color: b.minutes_left <= 30 ? "var(--kz-status-warn)" : "var(--kz-color-ink)",
                        }}
                      >
                        あと {b.minutes_left} 分
                      </span>
                    )}
                    <span
                      className="kz-num"
                      style={{ display: "block", fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}
                    >
                      {b.order_count} 件
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <h2 style={{ margin: "0 0 var(--kz-space-3)", fontSize: "var(--kz-font-size-md)" }}>
            今日の受付チャネル
          </h2>
          {Object.keys(d.by_channel).length === 0 ? (
            <p style={{ color: "var(--kz-color-ink-muted)", fontSize: "var(--kz-font-size-sm)" }}>
              まだ今日の注文はありません。
            </p>
          ) : (
            <div style={{ display: "grid", gap: "var(--kz-space-2)" }}>
              {Object.entries(d.by_channel).map(([ch, n]) => (
                <div key={ch} style={{ display: "flex", justifyContent: "space-between" }} data-testid={`channel-${ch}`}>
                  <span>{CHANNEL_LABEL[ch] ?? ch}</span>
                  <span className="kz-num" style={{ fontWeight: 700 }}>
                    {n} 件
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

function Stat({
  label,
  value,
  to,
  tone,
  testId,
}: {
  label: string;
  value: number;
  to: string;
  tone: "primary" | "warn" | "neutral";
  testId: string;
}) {
  const color =
    tone === "warn" ? "var(--kz-status-warn)" : tone === "primary" ? "var(--kz-color-primary)" : "var(--kz-color-ink)";
  return (
    // カード全体がリンク＝ホバーで影が1段上がり（`.kz-card-link`）、押すと沈む（`.kz-pressable`）。
    // 以前は「押せるのに何も返さない大きな面」だった。
    <Link to={to} className="kz-card-link kz-pressable" data-testid={testId}>
      <Card>
        <div style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>{label}</div>
        <div className="kz-num" style={{ fontSize: 40, fontWeight: 700, color, lineHeight: 1.2 }}>
          {value}
        </div>
      </Card>
    </Link>
  );
}
